import { SignIn } from '@clerk/clerk-react';
import type { ComponentProps } from 'react';

type ClerkAppearance = NonNullable<ComponentProps<typeof SignIn>['appearance']>;

export const clerkAppearance: ClerkAppearance = {
  variables: {
    colorPrimary: 'hsl(var(--primary))',
    colorDanger: 'hsl(var(--destructive))',
    colorSuccess: '#22c55e',
    colorWarning: '#f7760d',
    colorNeutral: '#ffffff',
    colorBackground: 'hsl(var(--background))',
    colorInputBackground: 'hsl(var(--secondary))',
    colorInputText: 'hsl(var(--foreground))',
    colorText: 'hsl(var(--foreground))',
    colorTextSecondary: 'rgba(255, 255, 255, 0.4)',
    colorTextOnPrimaryBackground: '#ffffff',
    borderRadius: '0.5rem',
    fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
    fontFamilyButtons: 'Poppins, ui-sans-serif, system-ui, sans-serif',
    fontSize: '0.875rem',
  },
  elements: {
    rootBox: {
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
    },
    cardBox: {
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
    },
    card: {
      background: 'transparent',
      boxShadow: 'none',
      border: 'none',
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
      paddingLeft: '0',
      paddingRight: '0',
      width: '100%',
      maxWidth: '100%',
      gap: '1.5rem',
      overflow: 'visible',
      boxSizing: 'border-box',
    },
    scrollBox: {
      overflow: 'visible',
      paddingLeft: '0',
      paddingRight: '0',
    },
    main: {
      gap: '1.5rem',
      overflow: 'visible',
      width: '100%',
      boxSizing: 'border-box',
      paddingLeft: '0',
      paddingRight: '0',
    },
    header: {
      textAlign: 'center',
      marginBottom: '0',
    },
    headerTitle: {
      fontFamily: '"Saira Extra Condensed", Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '800',
      fontSize: '2rem',
      lineHeight: '2rem',
      color: 'hsl(var(--foreground))',
      textAlign: 'center',
    },
    headerSubtitle: {
      display: 'none',
    },
    logoBox: {
      display: 'none',
    },
    form: {
      gap: '1.5rem',
    },
    formFieldRow: {
      marginBottom: '0',
    },
    formField: {
      marginBottom: '0',
    },
    formFieldLabelRow: {
      marginBottom: '0.5rem',
    },
    formFieldLabel: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '400',
      fontSize: '0.75rem',
      lineHeight: '0.75rem',
      color: 'hsl(var(--foreground))',
      textTransform: 'capitalize',
      marginBottom: '0',
    },
    formFieldInput: {
      backgroundColor: 'hsl(var(--secondary))',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: '0.5rem',
      height: '3rem',
      minHeight: '3rem',
      maxHeight: '3rem',
      padding: '1rem',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'hsl(var(--foreground))',
      // Active highlight is a white border — kill Clerk's primary-colored ring
      boxShadow: 'none',
      outline: 'none',
      boxSizing: 'border-box',
      transitionProperty: 'border-color',
      transitionDuration: '150ms',
      '&:hover': {
        backgroundColor: 'hsl(var(--secondary))',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&:focus': {
        backgroundColor: 'hsl(var(--secondary))',
        borderColor: '#ffffff',
        boxShadow: 'none',
        outline: 'none',
      },
      '&:focus-visible': {
        backgroundColor: 'hsl(var(--secondary))',
        borderColor: '#ffffff',
        boxShadow: 'none',
        outline: 'none',
      },
      '&[data-focus="true"], &[aria-invalid="false"]:focus': {
        borderColor: '#ffffff',
        boxShadow: 'none',
      },
    },
    formFieldInputShowPasswordButton: {
      width: '1.25rem',
      height: '1.25rem',
      minWidth: '1.25rem',
      minHeight: '1.25rem',
      padding: '0',
      color: '#ffffff',
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    input: {
      color: 'hsl(var(--foreground))',
    },
    formFieldInputGroup: {
      borderRadius: '0.5rem',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    checkbox: {
      width: '1.125rem',
      height: '1.125rem',
      borderRadius: '0.25rem',
      border: '1px solid #ffffff',
      backgroundColor: 'transparent',
      accentColor: 'hsl(var(--primary))',
    },
    checkboxLabel: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '400',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'hsl(var(--foreground))',
    },
    formFieldAction: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '0.875rem',
      lineHeight: '1rem',
      color: 'hsl(var(--foreground))',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    formButtonPrimary: {
      backgroundColor: 'hsl(var(--primary))',
      border: '2px solid hsl(var(--primary))',
      borderRadius: '999px',
      minHeight: '3rem',
      height: '3rem',
      padding: '0.75rem 1.5rem',
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      textTransform: 'capitalize',
      color: '#ffffff',
      boxShadow: 'none',
      width: '100%',
      boxSizing: 'border-box',
      gap: '0',
      '&:hover': {
        filter: 'brightness(0.92)',
      },
      '&:focus': {
        boxShadow: 'none',
      },
      '&:disabled, &[data-disabled]': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.4)',
      },
    },
    formButtonPrimaryIcon: {
      display: 'none',
    },
    buttonArrowIcon: {
      display: 'none',
    },
    footer: {
      background: 'transparent',
      marginTop: '0.25rem',
      width: '100%',
      overflow: 'visible',
    },
    footerAction: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.25rem',
      width: '100%',
      textAlign: 'center',
    },
    footerActionText: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      color: 'hsl(var(--foreground))',
      textAlign: 'center',
    },
    footerActionLink: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      color: 'hsl(var(--foreground))',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      textAlign: 'center',
      '&:hover': {
        color: 'hsl(var(--foreground))',
      },
    },
    identityPreviewEditButton: {
      color: 'hsl(var(--foreground))',
    },
    formFieldErrorText: {
      color: 'hsl(var(--destructive))',
      fontSize: '0.75rem',
    },
    alertText: {
      color: 'hsl(var(--destructive))',
    },
    dividerLine: {
      backgroundColor: 'hsl(var(--border))',
    },
    dividerText: {
      color: 'hsl(var(--muted-foreground))',
    },
    socialButtonsBlockButton: {
      backgroundColor: 'hsl(var(--secondary))',
      border: '1px solid hsl(var(--border))',
      borderRadius: '0.5rem',
      color: 'hsl(var(--foreground))',
      width: '100%',
      boxSizing: 'border-box',
    },
  },
};
